import { SearchableSelect } from "@/components/molecules/searchable-select";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  FRANCE_BORDERING_COUNTRIES,
  type GeoCallFilter,
  type GeoFilterMode,
} from "@/features/connectors/types";
import { useTranslation } from "@/features/utils/i18n";
import { localizedCountries } from "@/features/utils/display-names";
import { useMemo } from "react";

/**
 * Geographic filtering of inbound calls: accept (or refuse) callers based on
 * their country. Applied only when no caller-routing rule matched.
 *
 * Read-only for everyone but platform staff, and shown rather than hidden. Each
 * call this filter lets through costs a carrier leg plus a full STT/LLM/TTS
 * pipeline on our side, so the countries are ours to choose (the backend
 * enforces it — `SYSADMIN_ONLY_FLAGS` in `connectors/all.ts`). But a client
 * whose caller "just gets refused" with nothing on screen to explain it opens a
 * ticket, so the filter stays visible with a line saying who to ask.
 */
export const GeoFilterField = ({
  value,
  onChange,
  readOnly = false,
}: {
  value: GeoCallFilter;
  onChange: (filter: GeoCallFilter) => void;
  readOnly?: boolean;
}) => {
  const { t, i18n } = useTranslation("connectors");
  // In the interface's language, sorted by it.
  const countryItems = useMemo(
    () =>
      localizedCountries(i18n.language).map((country) => ({
        id: country.code,
        label: country.name,
      })),
    [i18n.language],
  );

  const update = (changes: Partial<GeoCallFilter>) => {
    if (readOnly) return;
    onChange({ ...value, ...changes });
  };

  return (
    <div className="space-y-2 border-t pt-3">
      <div className="flex items-start space-x-2">
        <Checkbox
          id="geo-filter-enabled"
          checked={value.enabled}
          disabled={readOnly}
          onCheckedChange={(checked) => update({ enabled: checked === true })}
        />
        <Label
          htmlFor="geo-filter-enabled"
          className="text-sm font-medium cursor-pointer"
        >
          {t("connectors.geo.title", "Filtrage géographique des appels")}
        </Label>
      </div>
      <p className="text-xs text-muted-foreground">
        {t(
          "connectors.geo.description",
          "Refusez les appels entrants selon le pays de l'appelant. Le filtre n'est appliqué qu'aux appels qui ne correspondent à aucune règle de routage ci-dessus.",
        )}
      </p>
      {readOnly && (
        <p className="text-xs text-muted-foreground italic">
          {t("connectors.geo.readOnly")}
        </p>
      )}

      {value.enabled && (
        <div className="space-y-3 pt-1">
          <div className="space-y-1">
            <Label className="text-xs">
              {t("connectors.geo.modeLabel", "Mode")}
            </Label>
            <Select
              value={value.mode}
              disabled={readOnly}
              onValueChange={(mode) => update({ mode: mode as GeoFilterMode })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="allow">
                  {t(
                    "connectors.geo.modes.allow",
                    "Autoriser uniquement ces pays",
                  )}
                </SelectItem>
                <SelectItem value="block">
                  {t("connectors.geo.modes.block", "Refuser ces pays")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">
              {t("connectors.geo.countriesLabel", "Pays")}
            </Label>
            <SearchableSelect
              multiple
              showBadges
              items={countryItems}
              disabled={readOnly}
              value={value.countries}
              onChange={(countries) =>
                update({
                  countries: Array.isArray(countries)
                    ? countries
                    : [countries].filter(Boolean),
                })
              }
              placeholder={t(
                "connectors.geo.countriesPlaceholder",
                "Sélectionnez les pays",
              )}
              searchPlaceholder={t(
                "connectors.geo.countriesSearch",
                "Rechercher un pays…",
              )}
              popoverWidth="w-full"
            />
            <div className="flex items-center gap-2 pt-1">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-7 text-xs"
                disabled={readOnly}
                onClick={() =>
                  update({ countries: [...FRANCE_BORDERING_COUNTRIES] })
                }
              >
                {t("connectors.geo.resetDefault", "France et pays frontaliers")}
              </Button>
              {value.countries.length === 0 && (
                <span className="text-xs text-destructive">
                  {t(
                    "connectors.geo.errors.countriesRequired",
                    "Sélectionnez au moins un pays.",
                  )}
                </span>
              )}
            </div>
          </div>

          <div className="flex items-start space-x-2">
            <Checkbox
              id="geo-filter-allow-unknown"
              checked={value.allow_unknown === true}
              disabled={readOnly}
              onCheckedChange={(checked) =>
                update({ allow_unknown: checked === true })
              }
            />
            <div className="space-y-1">
              <Label
                htmlFor="geo-filter-allow-unknown"
                className="text-sm font-normal cursor-pointer"
              >
                {t(
                  "connectors.geo.allowUnknown.label",
                  "Accepter les appels dont le pays est inconnu",
                )}
              </Label>
              <p className="text-xs text-muted-foreground">
                {t(
                  "connectors.geo.allowUnknown.description",
                  "Numéro masqué ou indicatif non reconnu. Décoché, ces appels sont refusés lorsque le mode « autoriser uniquement » est actif.",
                )}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
